import { getFirebaseBackend } from "../../../helpers/firebase_helper";
import {
  postFakeLogin,
  postJwtLogin,
} from "../../../helpers/fakebackend_helper";
import { adminLogin } from "../../../services/authService";
import { setAuthorization, clearAuthorization } from "../../../helpers/api_helper";
import { setCookie, deleteCookie } from "../../../helpers/cookie_helper";
import {
  saveMfaSession,
  clearMfaSession,
} from "../../../helpers/mfaSession_helper";

import {
  loginLoading,
  loginSuccess,
  logoutUserSuccess,
  apiError,
  reset_login_flag,
} from "./reducer";

/**
 * Persist FULL admin session (only after MFA enable/verify success).
 * Never call this with mfaSessionToken / step-1 response.
 */
export const persistLoginSession = (responseData, dispatch, history) => {
  const { token, refreshToken, thirdPartyToken, user: userData } = responseData || {};

  if (!token) {
    throw new Error("Login succeeded but no access token was returned.");
  }

  setCookie("authToken", token, 1);
  localStorage.setItem(
    "authUser",
    JSON.stringify({
      token,
      refreshToken,
      thirdPartyToken,
      user: userData,
    })
  );
  setAuthorization(token);
  clearMfaSession();
  dispatch(loginSuccess(userData));
  if (history) {
    history("/dashboard");
  }
};

/**
 * Complete login after MFA enable/verify — stores full tokens and redirects.
 */
export const completeMfaLogin = (loginData, history) => async (dispatch) => {
  try {
    dispatch(loginLoading());
    persistLoginSession(loginData, dispatch, history);
  } catch (error) {
    dispatch(
      apiError({
        message: error?.message || "Failed to complete login.",
        data: error,
      })
    );
  }
};

/**
 * Step 1 only: email + password → mfaSessionToken + nextStep.
 * Does NOT store full admin token.
 *
 * @returns {Promise<{ nextStep: string, email: string, mfaEnabled: boolean }|null>}
 */
export const loginUser = (user) => async (dispatch) => {
  try {
    dispatch(loginLoading());

    if (process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_DEFAULTAUTH === "api") {
      const response = await adminLogin({
        email: user.email,
        password: user.password,
      });

      if (response && response.status === true && response.data) {
        const data = response.data;

        // Guard: step-1 must never be treated as full login
        if (data.token && !data.mfaSessionToken) {
          // Unexpected legacy full-login shape — reject so UI does not store it
          dispatch(
            apiError({
              message: "Unexpected login response. Please contact support.",
              data: response,
            })
          );
          return null;
        }

        if (!data.mfaSessionToken || !data.nextStep) {
          dispatch(
            apiError({
              message: response?.message || "Login failed. Please try again.",
              data: response,
            })
          );
          return null;
        }

        saveMfaSession(data.mfaSessionToken, data.expiresIn || 300);
        dispatch(reset_login_flag());
        return {
          nextStep: data.nextStep,
          email: data.email || user.email,
          mfaEnabled: !!data.mfaEnabled,
          adminId: data.adminId,
        };
      }

      dispatch(
        apiError({
          message: response?.message || "Login failed. Please try again.",
          data: response,
        })
      );
      return null;
    }

    // Legacy non-API auth modes (unchanged)
    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      let fireBaseBackend = getFirebaseBackend();
      const response = fireBaseBackend.loginUser(user.email, user.password);
      const data = await response;
      if (data) {
        const token = data?.token || data?.accessToken || data?.data?.token;
        if (token) setCookie("authToken", token, 1);
        localStorage.setItem("authUser", JSON.stringify(data));
        dispatch(loginSuccess(data));
      }
      return null;
    }

    if (process.env.REACT_APP_DEFAULTAUTH === "jwt") {
      const response = postJwtLogin({ email: user.email, password: user.password });
      const data = await response;
      if (data) {
        const token = data?.token || data?.accessToken || data?.data?.token;
        if (token) setCookie("authToken", token, 1);
        localStorage.setItem("authUser", JSON.stringify(data));
        dispatch(loginSuccess(data));
      }
      return null;
    }

    if (process.env.REACT_APP_DEFAULTAUTH) {
      const response = postFakeLogin({ email: user.email, password: user.password });
      const data = await response;
      if (data) {
        const token = data?.token || data?.accessToken || data?.data?.token;
        if (token) setCookie("authToken", token, 1);
        localStorage.setItem("authUser", JSON.stringify(data));
        if (process.env.REACT_APP_DEFAULTAUTH === "fake") {
          let finallogin = JSON.parse(JSON.stringify(data));
          if (finallogin.status === "success") {
            dispatch(loginSuccess(finallogin.data));
          } else {
            dispatch(apiError(finallogin));
          }
        } else {
          dispatch(loginSuccess(data));
        }
      }
    }

    return null;
  } catch (error) {
    const detail = error?.data?.error;
    const errorMessage =
      (typeof detail === "string" && detail.trim()) ||
      error?.message ||
      error?.data?.message ||
      "An error occurred during login. Please try again.";
    dispatch(apiError({ message: errorMessage, data: error }));
    return null;
  }
};

export const logoutUser = () => async (dispatch) => {
  try {
    deleteCookie("authToken");
    localStorage.removeItem("authUser");
    clearMfaSession();
    clearAuthorization();

    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      let fireBaseBackend = getFirebaseBackend();
      const response = fireBaseBackend.logout;
      dispatch(logoutUserSuccess(response));
    } else {
      dispatch(logoutUserSuccess(true));
    }
  } catch (error) {
    dispatch(apiError(error));
  }
};

export const socialLogin = (type, history) => async (dispatch) => {
  try {
    let response;

    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      const fireBaseBackend = getFirebaseBackend();
      response = fireBaseBackend.socialLoginUser(type);
    }

    const socialdata = await response;
    if (socialdata) {
      const token = response?.token || response?.accessToken || response?.data?.token;
      if (token) {
        setCookie("authToken", token, 1);
      }
      localStorage.setItem("authUser", JSON.stringify(response));
      dispatch(loginSuccess(response));
      history("/dashboard");
    }
  } catch (error) {
    dispatch(apiError(error));
  }
};

export const resetLoginFlag = () => async (dispatch) => {
  try {
    return dispatch(reset_login_flag());
  } catch (error) {
    dispatch(apiError(error));
  }
};
